var _SkipToContent;
import React from 'react';
import { arrayOf, bool, element, elementType, exact, func, number, object, oneOf, oneOfType, shape, string } from 'prop-types';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import SovosNavigation from '../sovos-navigation/SovosNavigation';
import HEADER from '../sovos-navigation/constants/header';
const SkipToContent = styled('a')(_ref => {
  let {
    theme: {
      palette,
      spacing,
      zIndex
    }
  } = _ref;
  return {
    backgroundColor: palette.primary.main,
    border: "1px solid ".concat(palette.primary.dark),
    borderTop: 0,
    borderRadius: 0,
    color: palette.common.white,
    display: 'block',
    fontWeight: 500,
    left: '50%',
    top: 0,
    position: 'fixed',
    padding: spacing(),
    textDecoration: 'none',
    transform: 'translateX(-50%) translateY(-100%)',
    transition: 'transform 0.4s',
    zIndex: zIndex.modal,
    '&:focus, &:focus-visible': {
      outlineColor: 'transparent',
      transform: 'translateX(-50%) translateY(0)'
    }
  };
});
const Layout = styled('div', {
  shouldForwardProp: prop => prop !== 'width'
})(_ref2 => {
  let {
    width
  } = _ref2;
  return {
    display: 'flex',
    position: 'relative',
    width
  };
});
const MainContent = styled('main')(() => ({
  margin: 0,
  overflowX: 'hidden',
  padding: 0,
  flex: '1 1 auto'
}));
const SovosGlobalLayout = _ref3 => {
  let {
    children,
    className,
    'data-testid': dataTestId,
    NavigationProps,
    width = '100vw'
  } = _ref3;
  return /*#__PURE__*/React.createElement(React.Fragment, null, _SkipToContent || (_SkipToContent = /*#__PURE__*/React.createElement(SkipToContent, {
    href: "#main-content"
  }, "Skip to content")), /*#__PURE__*/React.createElement(Layout, {
    className: clsx('sovosGlobalLayout', className),
    "data-testid": dataTestId,
    width: width
  }, /*#__PURE__*/React.createElement(SovosNavigation, NavigationProps), /*#__PURE__*/React.createElement(MainContent, {
    tabIndex: -1,
    id: "main-content"
  }, children)));
};
SovosGlobalLayout.propTypes = {
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * The content of the global layout
   */
  children: element.isRequired,
  /**
   * @ignore
   */
  'data-testid': string,
  /**
   * Properties for the sovos navigation component
   */
  NavigationProps: shape({
    accounts: arrayOf(shape({
      name: string.isRequired,
      nestedAccounts: arrayOf(object),
      taxYear: string,
      type: oneOf(['production', 'test', 'default'])
    })),
    accountsAreLoading: bool,
    accountSwitcherLabels: object,
    additionalButtons: arrayOf(shape({
      key: oneOfType([string, number]).isRequired,
      disabled: bool,
      icon: elementType.isRequired,
      label: string,
      action: func.isRequired
    })),
    didAccountChange: func,
    FooterProps: shape({
      labels: exact({
        helpIcon: string,
        settingsIcon: string,
        userIcon: string
      }),
      onHelpClicked: func,
      settingsMenuItems: arrayOf(shape({
        label: string,
        action: func
      })),
      showHelpButton: bool,
      showSettingsButton: bool,
      showUserButton: bool,
      userMenuItems: arrayOf(shape({
        label: string,
        action: func
      })),
      userName: string.isRequired
    }),
    initialSelectedLink: string.isRequired,
    links: arrayOf(shape({
      action: func,
      confirmNavigationDialogProps: shape({
        cancelButtonText: string.isRequired,
        continueButtonText: string.isRequired,
        content: oneOfType([func, string]).isRequired,
        shouldShowDialog: func.isRequired,
        title: string.isRequired
      }),
      disabled: bool,
      icon: elementType.isRequired,
      id: oneOfType([string, number]),
      label: string.isRequired,
      nestedLinks: arrayOf(shape({
        action: func.isRequired,
        confirmNavigationDialogProps: shape({
          cancelButtonText: string.isRequired,
          continueButtonText: string.isRequired,
          content: oneOfType([func, string]).isRequired,
          shouldShowDialog: func.isRequired,
          title: string.isRequired
        }),
        disabled: bool,
        id: oneOfType([string, number]),
        label: string.isRequired,
        toolTip: string
      })),
      toolTip: string,
      type: oneOf(['content', HEADER])
    })).isRequired,
    minimumAccountSearchCharacters: number,
    onAccountSearch: func,
    onExpandAccount: func,
    onSelectProduct: func,
    product: oneOfType([string, object]),
    products: arrayOf(shape({
      disabled: bool,
      icon: elementType,
      id: oneOfType([number, string]),
      logoMonoUrl: string,
      logoUrl: string,
      name: string
    })),
    saveNavCollapsed: bool,
    selectedAccount: shape({
      name: string.isRequired,
      taxYear: string,
      type: oneOf(['production', 'test', 'default'])
    }),
    setAccount: func.isRequired,
    showProductSwitcher: bool,
    showSingleAccount: bool,
    slotProps: shape({
      additionFooterIconButtons: arrayOf(shape({
        children: element.isRequired,
        disabled: bool,
        onClick: func.isRequired,
        tooltipText: string.isRequired
      })),
      helpButton: exact({
        onClick: func
      }),
      productSwitcher: shape({
        onSelectProduct: func,
        products: arrayOf(shape({
          disabled: bool,
          icon: elementType,
          id: oneOfType([number, string]),
          logoMonoUrl: string,
          logoUrl: string,
          name: string
        })),
        selectedProduct: object,
        supportItems: arrayOf(shape({
          description: string,
          disabled: bool,
          icon: elementType,
          linkText: string.isRequired,
          target: string,
          url: string.isRequired
        })),
        tooltipText: string
      }),
      settingsMenu: oneOfType([object, exact({
        items: arrayOf(shape({
          label: string,
          action: func
        }))
      })]),
      userMenu: oneOfType([object, exact({
        email: string,
        items: arrayOf(shape({
          action: func,
          icon: elementType,
          label: string,
          type: string
        })),
        name: string,
        surname: string
      })])
    }),
    slots: shape({
      productSwitcher: elementType,
      settingsMenu: elementType,
      userMenu: elementType
    })
  }).isRequired,
  /**
   * Width style
   */
  width: oneOfType([string, number])
};
export default SovosGlobalLayout;